import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './quill-custom.css';
import AdminPage from './AdminPage';
import { useWindowWidth } from '../hooks/useWindowWidth';

const TEMPLATE_LABELS = {
  nieuwsbrief: 'Nieuwsbrief',
  annulering: 'Annulering',
  speeldag: 'Speeldag Update',
  uitslag: 'Uitslag',
};

const ROLE_LABELS = {
  admin: 'Admin',
  redacteur: 'Redacteur',
  commissie: 'Commissie',
  readonly: 'Readonly',
};

export default function PortalPage() {
  const { user, logout } = useAuth();

  // ─── Navigation ─────────────────────────────────────────────────────────────
  const [view, setView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const width = useWindowWidth();
  const isMobile = width < 768;

  // ─── Shared data ────────────────────────────────────────────────────────────
  const [sponsors, setSponsors] = useState([]);
  const [history, setHistory] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [recipientCount, setRecipientCount] = useState(null);
  const [recipientLoading, setRecipientLoading] = useState(false);

  // ─── Mailing lists ────────────────────────────────────────────────────────────
  const [mailingLists, setMailingLists] = useState([]);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);

  // ─── Compose state ───────────────────────────────────────────────────────────
  const [composeStep, setComposeStep] = useState('typeSelect'); // 'typeSelect'|'form'|'preview'|'sent'
  const [composeTemplate, setComposeTemplate] = useState(null);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [subject, setSubject] = useState('');
  // Nieuwsbrief fields
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  // Shared template fields
  const [dagsponserId, setDagsponserId] = useState('');
  const [dagsponsorDesc, setDagsponsorDesc] = useState('');
  // Speeldag + Uitslag shared fields
  const [speeldagNummer, setSpeeldagNummer] = useState('');
  const [datum, setDatum] = useState('');
  // Annulering-specific fields
  const [reden, setReden] = useState('');
  const [inhaaldatum, setInhaaldatum] = useState('');
  // Speeldag-specific fields
  const [locatie, setLocatie] = useState('Wouwse Plantage');
  const [aanvangstijd, setAanvangstijd] = useState('');
  const [extraInfo, setExtraInfo] = useState('');
  // Uitslag-specific fields
  const [intro, setIntro] = useState('');
  const [leaderboardRows, setLeaderboardRows] = useState([{ teamName: '', poule: '', score: '' }]);
  const [topMoment, setTopMoment] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [composeError, setComposeError] = useState('');

  // ─── Delete draft confirmation modal ─────────────────────────────────────────
  const [deleteDraftId, setDeleteDraftId] = useState(null);

  // ─── Auto-save state ─────────────────────────────────────────────────────────
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const toastTimerRef = useRef(null);

  // Use a ref to avoid stale closures in the interval
  const formRef = useRef({});
  formRef.current = {
    subject, title, body, dagsponserId, dagsponsorDesc,
    reden, inhaaldatum,
    speeldagNummer, datum, locatie, aanvangstijd, extraInfo,
    intro, leaderboardRows, topMoment,
    composeTemplate, currentDraftId,
  };
  const currentDraftIdRef = useRef(currentDraftId);
  currentDraftIdRef.current = currentDraftId;

  // ─── Initial data fetch ──────────────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/sponsors').then(r => setSponsors(r.data)).catch(() => {});
    axios.get('/api/email/history').then(r => setHistory(r.data)).catch(() => {});
    axios.get('/api/drafts').then(r => setDrafts(r.data)).catch(() => {});
  }, []);

  // ─── Fetch mailing lists when compose form opens ──────────────────────────────
  useEffect(() => {
    if (composeStep === 'form' && mailingLists.length === 0 && !listsLoading) {
      setListsLoading(true);
      axios.get('/api/email/lists')
        .then(r => setMailingLists(r.data))
        .catch(() => {})
        .finally(() => setListsLoading(false));
    }
  }, [composeStep, mailingLists.length, listsLoading]);

  // ─── Fetch recipient count on demand ─────────────────────────────────────────
  useEffect(() => {
    if (view === 'ontvangers' && recipientCount === null && !recipientLoading) {
      setRecipientLoading(true);
      axios.get('/api/email/recipients')
        .then(r => setRecipientCount(r.data.count))
        .catch(() => setRecipientCount(-1))
        .finally(() => setRecipientLoading(false));
    }
  }, [view, recipientCount, recipientLoading]);

  // ─── Auto-save: true interval, reads from ref ─────────────────────────────────
  const saveDraft = useCallback(async () => {
    const {
      subject, title, body, dagsponserId, dagsponsorDesc,
      reden, inhaaldatum,
      speeldagNummer, datum, locatie, aanvangstijd, extraInfo,
      intro, leaderboardRows, topMoment,
      composeTemplate,
    } = formRef.current;
    const draftId = currentDraftIdRef.current;
    if (!composeTemplate) return;
    if (!subject && !title && !body && !speeldagNummer && !datum && !reden) return;

    setIsSaving(true);
    try {
      const payload = {
        template: composeTemplate,
        subject,
        data: {
          title, body, dagsponserId, dagsponsorDesc,
          reden, inhaaldatum,
          speeldagNummer, datum, locatie, aanvangstijd, extraInfo,
          intro, leaderboardRows, topMoment,
        },
      };
      if (draftId) {
        await axios.put(`/api/drafts/${draftId}`, payload);
      } else {
        const res = await axios.post('/api/drafts', payload);
        setCurrentDraftId(res.data.id);
        currentDraftIdRef.current = res.data.id;
      }
      setLastSaved(new Date());
      axios.get('/api/drafts').then(r => setDrafts(r.data)).catch(() => {});
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, []); // stable — reads all values from refs

  useEffect(() => {
    if (composeStep !== 'form') return;
    const id = setInterval(saveDraft, 30000);
    return () => clearInterval(id);
  }, [composeStep, saveDraft]);

  // ─── Compose helpers ──────────────────────────────────────────────────────────
  const startNewCompose = () => {
    setComposeStep('typeSelect');
    setComposeTemplate(null);
    setCurrentDraftId(null);
    currentDraftIdRef.current = null;
    setSubject('');
    setTitle('');
    setBody('');
    setDagsponserId('');
    setDagsponsorDesc('');
    setReden('');
    setInhaaldatum('');
    setSpeeldagNummer('');
    setDatum('');
    setLocatie('Wouwse Plantage');
    setAanvangstijd('');
    setExtraInfo('');
    setIntro('');
    setLeaderboardRows([{ teamName: '', poule: '', score: '' }]);
    setTopMoment('');
    setPreviewHtml('');
    setComposeError('');
    setLastSaved(null);
    setSelectedListIds([]);
    setView('compose');
  };

  const loadDraft = (draft) => {
    setCurrentDraftId(draft.id);
    currentDraftIdRef.current = draft.id;
    setComposeTemplate(draft.template);
    setSubject(draft.subject || '');
    setTitle(draft.data?.title || '');
    setBody(draft.data?.body || '');
    setDagsponserId(draft.data?.dagsponserId || '');
    setDagsponsorDesc(draft.data?.dagsponsorDesc || '');
    setReden(draft.data?.reden || '');
    setInhaaldatum(draft.data?.inhaaldatum || '');
    setSpeeldagNummer(draft.data?.speeldagNummer || '');
    setDatum(draft.data?.datum || '');
    setLocatie(draft.data?.locatie || 'Wouwse Plantage');
    setAanvangstijd(draft.data?.aanvangstijd || '');
    setExtraInfo(draft.data?.extraInfo || '');
    setIntro(draft.data?.intro || '');
    setLeaderboardRows(draft.data?.leaderboardRows || [{ teamName: '', poule: '', score: '' }]);
    setTopMoment(draft.data?.topMoment || '');
    setPreviewHtml('');
    setComposeError('');
    setLastSaved(draft.updatedAt ? new Date(draft.updatedAt) : null);
    setComposeStep('form');
    setView('compose');
  };

  const deleteDraft = (draftId) => {
    setDeleteDraftId(draftId);
  };

  const confirmDeleteDraft = async () => {
    const draftId = deleteDraftId;
    setDeleteDraftId(null);
    try {
      await axios.delete(`/api/drafts/${draftId}`);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
        currentDraftIdRef.current = null;
      }
    } catch (err) {
      console.error('Delete draft failed:', err);
    }
  };

  const buildEmailData = () => {
    const selectedSponsor = sponsors.find(s => s.id === dagsponserId);
    const dagsponsorObj = selectedSponsor ? {
      name: selectedSponsor.name,
      website: selectedSponsor.website,
      description: dagsponsorDesc,
    } : null;

    if (composeTemplate === 'annulering') {
      return { speeldagNummer, datum, reden, inhaaldatum: inhaaldatum || null, extraInfo: extraInfo || null, sponsors };
    }
    if (composeTemplate === 'speeldag') {
      return { speeldagNummer, datum, locatie, aanvangstijd, extraInfo, sponsors, dagsponsor: dagsponsorObj };
    }
    if (composeTemplate === 'uitslag') {
      return {
        speeldagNummer, datum, intro,
        leaderboard: leaderboardRows.map((row, i) => ({ rank: i + 1, ...row })),
        topMoment,
        sponsors,
        dagsponsor: dagsponsorObj,
      };
    }
    // nieuwsbrief (default)
    return {
      issueLabel: new Date().toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }),
      headline: title,
      introText: body,
      seizoen: '25/26',
      stats: [],
      events: [],
      leaderboard: [],
      sponsors,
      dagsponsor: dagsponsorObj,
    };
  };

  const handlePreview = async () => {
    if (!subject) return setComposeError('Vul de onderwerpregel in.');
    if (composeTemplate === 'nieuwsbrief' && (!title || !body)) return setComposeError('Vul alle velden in.');
    if (composeTemplate === 'annulering' && (!speeldagNummer || !datum || !reden)) return setComposeError('Vul het speeldagnummer, de datum en de reden in.');
    if ((composeTemplate === 'speeldag' || composeTemplate === 'uitslag') && (!speeldagNummer || !datum)) return setComposeError('Vul het speeldagnummer en de datum in.');
    if (selectedListIds.length === 0) return setComposeError('Selecteer minimaal één mailinglijst.');
    setComposeError('');
    setPreviewing(true);
    try {
      const res = await axios.post('/api/email/preview', {
        template: composeTemplate,
        data: buildEmailData(),
      });
      setPreviewHtml(res.data.html);
      setComposeStep('preview');
    } catch {
      setComposeError('Preview mislukt. Probeer opnieuw.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    setComposeError('');
    setSending(true);
    try {
      await axios.post('/api/email/send', {
        template: composeTemplate,
        data: buildEmailData(),
        subjectLine: subject,
        listIds: selectedListIds,
      });
      if (currentDraftId) {
        axios.delete(`/api/drafts/${currentDraftId}`).catch(() => {});
        setDrafts(prev => prev.filter(d => d.id !== currentDraftId));
        setCurrentDraftId(null);
        currentDraftIdRef.current = null;
      }
      axios.get('/api/email/history').then(r => setHistory(r.data)).catch(() => {});
      setComposeStep('sent');
    } catch (err) {
      setComposeError(err.response?.data?.error || 'Versturen mislukt.');
    } finally {
      setSending(false);
    }
  };

  // ─── Sidebar ──────────────────────────────────────────────────────────────────
  const navItems = [
    { id: 'dashboard', icon: '⊞', label: 'Dashboard' },
    { id: 'compose',   icon: '✎',  label: 'Nieuwe e-mail' },
    { id: 'drafts',    icon: '◫',  label: 'Concepten', badge: drafts.length || null },
    { id: 'verzonden', icon: '→',  label: 'Verzonden' },
    { id: 'ontvangers',icon: '◎',  label: 'Ontvangers' },
    ...(user.role === 'admin' ? [{ id: 'beheer', icon: '⚙', label: 'Beheer' }] : []),
  ];

  const handleNav = (id) => {
    if (id === 'compose') {
      startNewCompose();
    } else {
      setView(id);
    }
    setSidebarOpen(false);
  };

  // ─── Dashboard view ───────────────────────────────────────────────────────────
  const renderDashboard = () => {
    const totalSent = history.length;
    const lastSentItem = history[0];
    const firstName = user.name.split(' ')[0];

    return (
      <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
        {/* Welcome banner */}
        <div style={{ ...v.welcomeBanner, ...(isMobile ? { flexDirection: 'column', alignItems: 'flex-start' } : {}) }}>
          <div>
            <div style={v.welcomeTitle}>Welkom terug, {firstName}</div>
            <div style={v.welcomeSub}>Wat wil je vandaag versturen?</div>
          </div>
          <button style={{ ...v.newEmailBtn, ...(isMobile ? { width: '100%' } : {}) }} onClick={startNewCompose}>
            + Nieuwe e-mail opstellen
          </button>
        </div>

        {/* Stats row */}
        <div style={isMobile ? { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24 } : v.statsRow}>
          <div style={v.statCard}>
            <div style={v.statNum}>{totalSent}</div>
            <div style={v.statLabel}>Verstuurd</div>
          </div>
          <div style={v.statCard}>
            <div style={v.statNum}>
              {lastSentItem
                ? new Date(lastSentItem.sentAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
                : '—'}
            </div>
            <div style={v.statLabel}>Laatste verzending</div>
          </div>
          <div style={v.statCard}>
            <div style={v.statNum}>
              {recipientCount === null ? '—' : recipientCount === -1 ? '?' : `~${recipientCount}`}
            </div>
            <div style={v.statLabel}>Ontvangers</div>
          </div>
          <div style={v.statCard}>
            <div style={v.statNum}>{drafts.length}</div>
            <div style={v.statLabel}>Concepten</div>
          </div>
        </div>

        {/* Recent sent emails */}
        <div style={v.section}>
          <div style={v.sectionTitle}>Recente e-mails</div>
          {history.length === 0 ? (
            <div style={v.emptyCard}>Nog geen e-mails verstuurd.</div>
          ) : (
            history.slice(0, 5).map(item => (
              <div key={item.id} style={v.historyRow}>
                <div style={v.historyLeft}>
                  <div style={v.historySubject}>{item.subject}</div>
                  <div style={v.historyMeta}>
                    {TEMPLATE_LABELS[item.template] || item.template} · door {item.sentBy}
                  </div>
                </div>
                <div style={v.historyDate}>
                  {new Date(item.sentAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Draft resumption */}
        {drafts.length > 0 && (
          <div style={v.section}>
            <div style={v.sectionTitle}>Openstaande concepten</div>
            {drafts.slice(0, 3).map(draft => (
              <div key={draft.id} style={v.historyRow}>
                <div style={v.historyLeft}>
                  <div style={v.historySubject}>{draft.subject || '(geen onderwerp)'}</div>
                  <div style={v.historyMeta}>
                    {TEMPLATE_LABELS[draft.template] || draft.template} · door {draft.createdByName || 'onbekend'} · opgeslagen {new Date(draft.updatedAt).toLocaleDateString('nl-NL')}
                  </div>
                </div>
                <button style={v.resumeBtn} onClick={() => loadDraft(draft)}>Hervatten →</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── Compose view ─────────────────────────────────────────────────────────────
  const renderCompose = () => {
    if (composeStep === 'typeSelect') return renderTypeSelect();
    if (composeStep === 'form') return renderComposeForm();
    if (composeStep === 'preview') return renderPreview();
    if (composeStep === 'sent') return renderSent();
    return null;
  };

  const EMAIL_TYPES = [
    {
      id: 'nieuwsbrief',
      label: 'Nieuwsbrief',
      desc: 'Maandelijkse update voor alle deelnemers',
      available: true,
    },
    {
      id: 'annulering',
      label: 'Annulering',
      desc: 'Meld de afgelasting van een speeldag',
      available: true,
    },
    {
      id: 'speeldag',
      label: 'Speeldag Update',
      desc: 'Informatie voor de aankomende speeldag',
      available: true,
    },
    {
      id: 'uitslag',
      label: 'Uitslag',
      desc: 'Deel de resultaten van een speeldag',
      available: true,
    },
  ];

  const renderTypeSelect = () => (
    <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
      <div style={v.pageHeader}>
        <div style={v.pageTitle}>Nieuwe e-mail</div>
        <div style={v.pageSub}>Kies het type e-mail dat je wilt opstellen</div>
      </div>
      <div style={{ ...v.typeGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)' }}>
        {EMAIL_TYPES.map(type => (
          <button
            key={type.id}
            style={{
              ...v.typeCard,
              ...(type.available ? v.typeCardAvailable : v.typeCardDisabled),
            }}
            onClick={() => {
              if (!type.available) return;
              setComposeTemplate(type.id);
              setComposeStep('form');
            }}
            disabled={!type.available}
          >
            {!type.available && <div style={v.comingSoonBadge}>Binnenkort</div>}
            <div style={v.typeCardLabel}>{type.label}</div>
            <div style={v.typeCardDesc}>{type.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderComposeForm = () => {
    const savedLabel = isSaving
      ? 'Opslaan...'
      : lastSaved
        ? `Opgeslagen om ${lastSaved.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`
        : null;

    return (
      <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
        <div style={v.pageHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button style={v.backLink} onClick={() => setComposeStep('typeSelect')}>
              ← Terug
            </button>
            <div>
              <div style={v.pageTitle}>{TEMPLATE_LABELS[composeTemplate]}</div>
              {savedLabel && <div style={v.savedIndicator}>{savedLabel}</div>}
            </div>
          </div>
        </div>

        <div style={{ ...v.formCard, padding: isMobile ? '20px 16px' : '32px 36px' }}>
          <div style={v.field}>
            <label style={v.label}>Onderwerpregel</label>
            <input
              style={v.input}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="bv. Nieuwsbrief – Speeldag 5 update"
            />
          </div>

          {/* ── Nieuwsbrief fields ── */}
          {composeTemplate === 'nieuwsbrief' && (<>
            <div style={v.field}>
              <label style={v.label}>Titel van de e-mail</label>
              <input
                style={v.input}
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="bv. Speeldag 5 was een groot succes!"
              />
            </div>
            <div style={v.field}>
              <label style={v.label}>Bericht</label>
              <ReactQuill
                value={body}
                onChange={setBody}
                placeholder="Schrijf hier je bericht aan de deelnemers..."
                modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }}
                style={{ background: '#fff', borderRadius: 8, marginBottom: 8, height: '240px' }}
              />
            </div>
          </>)}

          {/* ── Annulering fields ── */}
          {composeTemplate === 'annulering' && (<>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: isMobile ? '1' : '0 0 120px' }}>
                <label style={v.label}>Speeldag nr.</label>
                <input
                  style={v.input}
                  value={speeldagNummer}
                  onChange={e => setSpeeldagNummer(e.target.value)}
                  placeholder="bv. 6"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={v.label}>Geannuleerde datum</label>
                <input
                  style={v.input}
                  value={datum}
                  onChange={e => setDatum(e.target.value)}
                  placeholder="bv. Zaterdag 14 maart 2026"
                />
              </div>
            </div>
            <div style={v.field}>
              <label style={v.label}>Reden van annulering</label>
              <ReactQuill
                value={reden}
                onChange={setReden}
                placeholder="Leg kort uit waarom de speeldag niet doorgaat..."
                modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }}
                style={{ background: '#fff', borderRadius: 8, marginBottom: 8, height: '180px' }}
              />
            </div>
            <div style={{ ...v.field, marginTop: 40 }}>
              <label style={v.label}>Inhaaldatum (optioneel)</label>
              <input
                style={v.input}
                value={inhaaldatum}
                onChange={e => setInhaaldatum(e.target.value)}
                placeholder="bv. Zaterdag 28 maart 2026"
              />
            </div>
            <div style={v.field}>
              <label style={v.label}>Aanvullende informatie (optioneel)</label>
              <ReactQuill
                value={extraInfo}
                onChange={setExtraInfo}
                placeholder="bv. verdere mededelingen, contact opnemen met..."
                modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }}
                style={{ background: '#fff', borderRadius: 8, marginBottom: 8, height: '160px' }}
              />
            </div>
          </>)}

          {/* ── Speeldag Update fields ── */}
          {composeTemplate === 'speeldag' && (<>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: isMobile ? '1' : '0 0 120px' }}>
                <label style={v.label}>Speeldag nr.</label>
                <input
                  style={v.input}
                  value={speeldagNummer}
                  onChange={e => setSpeeldagNummer(e.target.value)}
                  placeholder="bv. 6"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={v.label}>Datum</label>
                <input
                  style={v.input}
                  value={datum}
                  onChange={e => setDatum(e.target.value)}
                  placeholder="bv. Zaterdag 14 maart 2026"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: 1 }}>
                <label style={v.label}>Locatie</label>
                <input
                  style={v.input}
                  value={locatie}
                  onChange={e => setLocatie(e.target.value)}
                  placeholder="Wouwse Plantage"
                />
              </div>
              <div style={{ flex: isMobile ? '1' : '0 0 160px' }}>
                <label style={v.label}>Aanvangstijd</label>
                <input
                  style={v.input}
                  value={aanvangstijd}
                  onChange={e => setAanvangstijd(e.target.value)}
                  placeholder="bv. 09:00"
                />
              </div>
            </div>
            <div style={v.field}>
              <label style={v.label}>Extra informatie (optioneel)</label>
              <ReactQuill
                value={extraInfo}
                onChange={setExtraInfo}
                placeholder="bv. baancondities, wat mee te nemen, bijzonderheden..."
                modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }}
                style={{ background: '#fff', borderRadius: 8, marginBottom: 8, height: '200px' }}
              />
            </div>
          </>)}

          {/* ── Uitslag fields ── */}
          {composeTemplate === 'uitslag' && (<>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: isMobile ? '1' : '0 0 120px' }}>
                <label style={v.label}>Speeldag nr.</label>
                <input
                  style={v.input}
                  value={speeldagNummer}
                  onChange={e => setSpeeldagNummer(e.target.value)}
                  placeholder="bv. 6"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={v.label}>Datum</label>
                <input
                  style={v.input}
                  value={datum}
                  onChange={e => setDatum(e.target.value)}
                  placeholder="bv. Zaterdag 14 maart 2026"
                />
              </div>
            </div>
            <div style={v.field}>
              <label style={v.label}>Terugblik (optioneel)</label>
              <ReactQuill
                value={intro}
                onChange={setIntro}
                placeholder="Korte terugblik op de speeldag..."
                modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }}
                style={{ background: '#fff', borderRadius: 8, marginBottom: 8, height: '180px' }}
              />
            </div>
            <div style={{ ...v.field, marginTop: 40 }}>
              <label style={v.label}>Eindstand</label>
              <div style={{ overflowX: 'auto' }}>
              <div style={{ ...v.lbTable, minWidth: 380 }}>
                <div style={v.lbHeader}>
                  <span style={v.lbColRank}>#</span>
                  <span style={v.lbColTeam}>Team</span>
                  <span style={v.lbColPoule}>Poule</span>
                  <span style={v.lbColScore}>Score</span>
                  <span style={{ width: 28 }} />
                </div>
                {leaderboardRows.map((row, i) => (
                  <div key={i} style={v.lbRow}>
                    <span style={v.lbColRank}>{i + 1}</span>
                    <input
                      style={{ ...v.lbInput, flex: 1 }}
                      value={row.teamName}
                      onChange={e => {
                        const next = [...leaderboardRows];
                        next[i] = { ...next[i], teamName: e.target.value };
                        setLeaderboardRows(next);
                      }}
                      placeholder="Teamnaam"
                    />
                    <input
                      style={{ ...v.lbInput, width: 80 }}
                      value={row.poule}
                      onChange={e => {
                        const next = [...leaderboardRows];
                        next[i] = { ...next[i], poule: e.target.value };
                        setLeaderboardRows(next);
                      }}
                      placeholder="Poule"
                    />
                    <input
                      style={{ ...v.lbInput, width: 80 }}
                      value={row.score}
                      onChange={e => {
                        const next = [...leaderboardRows];
                        next[i] = { ...next[i], score: e.target.value };
                        setLeaderboardRows(next);
                      }}
                      placeholder="Score"
                    />
                    <button
                      style={v.lbRemoveBtn}
                      onClick={() => setLeaderboardRows(prev => prev.filter((_, j) => j !== i))}
                      title="Verwijder rij"
                    >×</button>
                  </div>
                ))}
                <button
                  style={v.lbAddBtn}
                  onClick={() => setLeaderboardRows(prev => [...prev, { teamName: '', poule: '', score: '' }])}
                >
                  + Team toevoegen
                </button>
              </div>
              </div>
            </div>
            <div style={v.field}>
              <label style={v.label}>Hoogtepunt van de dag (optioneel)</label>
              <input
                style={v.input}
                value={topMoment}
                onChange={e => setTopMoment(e.target.value)}
                placeholder="bv. Eagle op hole 7 door Team X"
              />
            </div>
          </>)}

          {/* ── Dagsponsor (all templates) ── */}
          {sponsors.length > 0 && (
            <div style={{ ...v.field, marginTop: composeTemplate === 'nieuwsbrief' ? 52 : 8 }}>
              <label style={v.label}>Dagsponsor (optioneel)</label>
              <select
                style={v.input}
                value={dagsponserId}
                onChange={e => setDagsponserId(e.target.value)}
              >
                <option value="">— Geen dagsponsor —</option>
                {sponsors.map(sp => (
                  <option key={sp.id} value={sp.id}>{sp.name}</option>
                ))}
              </select>
              {dagsponserId && (
                <textarea
                  style={{ ...v.textarea, marginTop: 8, height: 80 }}
                  value={dagsponsorDesc}
                  onChange={e => setDagsponsorDesc(e.target.value)}
                  placeholder="Korte beschrijving van de dagsponsor..."
                  rows={3}
                />
              )}
            </div>
          )}

          {/* Mailing list selection */}
          <div style={v.field}>
            <label style={v.label}>Mailinglijsten</label>
            {listsLoading ? (
              <div style={{ fontSize: 13, color: '#aaa' }}>Lijsten ophalen...</div>
            ) : mailingLists.length === 0 ? (
              <div style={{ fontSize: 13, color: '#cc0000' }}>Kon de mailinglijsten niet ophalen.</div>
            ) : (
              <div style={v.listChecklist}>
                {mailingLists.map(list => {
                  const checked = selectedListIds.includes(list.id);
                  return (
                    <label key={list.id} style={v.listCheckRow}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setSelectedListIds(prev =>
                            checked ? prev.filter(id => id !== list.id) : [...prev, list.id]
                          );
                        }}
                        style={{ accentColor: '#3a7a4a', width: 16, height: 16, flexShrink: 0 }}
                      />
                      <span style={v.listCheckName}>{list.name}</span>
                      <span style={v.listCheckCount}>{list.subscriberCount} ontvangers</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {composeError && <div style={v.error}>{composeError}</div>}

          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexDirection: isMobile ? 'column' : 'row' }}>
            <button
              style={{ ...v.primaryBtn, opacity: previewing ? 0.7 : 1, minHeight: 44 }}
              onClick={handlePreview}
              disabled={previewing}
            >
              {previewing ? 'Laden...' : 'Voorbeeld bekijken →'}
            </button>
            <button style={{ ...v.secondaryBtn, minHeight: 44 }} onClick={async () => {
              await saveDraft();
              setShowSavedToast(true);
              clearTimeout(toastTimerRef.current);
              toastTimerRef.current = setTimeout(() => setShowSavedToast(false), 3000);
            }}>
              Opslaan als concept
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPreview = () => (
    <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
      <div style={{ ...v.previewToolbar, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
        <button style={{ ...v.secondaryBtn, minHeight: 44 }} onClick={() => setComposeStep('form')}>
          ← Terug aanpassen
        </button>
        <div style={v.previewMeta}>
          <span style={v.previewSubject}>"{subject}"</span>
          <span style={v.previewNote}>Controleer de e-mail zorgvuldig voordat je verstuurt</span>
        </div>
        <button
          style={{ ...v.primaryBtn, opacity: sending ? 0.7 : 1, minHeight: 44 }}
          onClick={handleSend}
          disabled={sending}
        >
          {sending
            ? 'Versturen...'
            : `✉ Verstuur naar ${selectedListIds.length} lijst${selectedListIds.length !== 1 ? 'en' : ''}`}
        </button>
      </div>
      {composeError && <div style={{ ...v.error, marginBottom: 12 }}>{composeError}</div>}
      <iframe
        srcDoc={previewHtml}
        style={{ ...v.previewFrame, height: isMobile ? '500px' : '800px' }}
        title="Email preview"
      />
    </div>
  );

  const renderSent = () => (
    <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
      <div style={v.centeredCard}>
        <div style={v.successIcon}>✓</div>
        <div style={v.successTitle}>Verstuurd!</div>
        <p style={v.successText}>
          De e-mail is succesvol verstuurd naar alle deelnemers.
        </p>
        <button style={v.primaryBtn} onClick={startNewCompose}>
          Nieuwe e-mail opstellen
        </button>
        <button style={{ ...v.secondaryBtn, marginTop: 12 }} onClick={() => setView('dashboard')}>
          Terug naar dashboard
        </button>
      </div>
    </div>
  );

  // ─── Drafts view ──────────────────────────────────────────────────────────────
  const renderDrafts = () => (
    <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
      <div style={v.pageHeader}>
        <div style={v.pageTitle}>Concepten</div>
        <div style={v.pageSub}>{drafts.length} opgeslagen concept{drafts.length !== 1 ? 'en' : ''}</div>
      </div>
      {drafts.length === 0 ? (
        <div style={v.emptyCard}>Geen concepten opgeslagen.</div>
      ) : (
        drafts.map(draft => (
          <div key={draft.id} style={v.historyRow}>
            <div style={v.historyLeft}>
              <div style={v.historySubject}>{draft.subject || '(geen onderwerp)'}</div>
              <div style={v.historyMeta}>
                {TEMPLATE_LABELS[draft.template] || draft.template} · door {draft.createdByName || 'onbekend'} · opgeslagen {new Date(draft.updatedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} om {new Date(draft.updatedAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={v.resumeBtn} onClick={() => loadDraft(draft)}>Hervatten →</button>
              {(draft.userId === user.id || user.role === 'admin') && (
                <button style={v.deleteBtn} onClick={() => deleteDraft(draft.id)}>Verwijderen</button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );

  // ─── Verzonden view ───────────────────────────────────────────────────────────
  const renderVerzonden = () => (
    <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
      <div style={v.pageHeader}>
        <div style={v.pageTitle}>Verzonden</div>
        <div style={v.pageSub}>{history.length} e-mail{history.length !== 1 ? 's' : ''} verstuurd</div>
      </div>
      {history.length === 0 ? (
        <div style={v.emptyCard}>Nog geen e-mails verstuurd.</div>
      ) : (
        history.map(item => (
          <div key={item.id} style={v.historyRow}>
            <div style={v.historyLeft}>
              <div style={v.historySubject}>{item.subject}</div>
              <div style={v.historyMeta}>
                {TEMPLATE_LABELS[item.template] || item.template} · door {item.sentBy}
              </div>
            </div>
            <div style={v.historyDate}>
              {new Date(item.sentAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
        ))
      )}
    </div>
  );

  // ─── Ontvangers view ──────────────────────────────────────────────────────────
  const renderOntvangers = () => (
    <div style={{ ...v.wrap, padding: isMobile ? '24px 16px' : '40px 32px' }}>
      <div style={v.pageHeader}>
        <div style={v.pageTitle}>Ontvangers</div>
        <div style={v.pageSub}>Overzicht van de mailinglijst</div>
      </div>
      <div style={v.centeredCard}>
        {recipientLoading ? (
          <div style={v.statNum}>Laden...</div>
        ) : recipientCount === -1 ? (
          <div style={{ color: '#cc0000', fontSize: 14 }}>Kon het aantal ontvangers niet ophalen.</div>
        ) : (
          <>
            <div style={{ ...v.statNum, fontSize: 56, marginBottom: 8 }}>
              {recipientCount ?? '—'}
            </div>
            <div style={v.statLabel}>actieve ontvangers in de Brevo-lijst</div>
          </>
        )}
      </div>
    </div>
  );

  // ─── Shell ────────────────────────────────────────────────────────────────────
  return (
    <div style={s.shell}>
      {/* Saved toast */}
      {showSavedToast && (
        <div style={{
          position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          background: '#1a2a1e', color: '#fff', borderRadius: 10,
          padding: '14px 24px', fontSize: 14, fontWeight: 500,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)', zIndex: 2000,
          display: 'flex', alignItems: 'center', gap: 10,
          whiteSpace: 'nowrap',
        }}>
          <span style={{ color: '#6dbf7e', fontSize: 16 }}>✓</span>
          Concept opgeslagen
        </div>
      )}

      {/* Delete draft confirmation modal */}
      {deleteDraftId && (
        <div style={m.overlay}>
          <div style={m.box}>
            <div style={m.title}>Concept verwijderen?</div>
            <p style={m.text}>Dit concept wordt permanent verwijderd en kan niet worden hersteld.</p>
            <div style={m.actions}>
              <button style={m.cancelBtn} onClick={() => setDeleteDraftId(null)}>Annuleren</button>
              <button style={m.confirmBtn} onClick={confirmDeleteDraft}>Verwijderen</button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile header */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 56,
          background: '#1a2a1e', display: 'flex', alignItems: 'center',
          padding: '0 16px', zIndex: 100,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              fontSize: 22, cursor: 'pointer', padding: '8px', marginRight: 12,
              lineHeight: 1, minHeight: 44, minWidth: 44,
            }}
          >
            ☰
          </button>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#fff', fontWeight: 'bold' }}>
            Lumumba<span style={{ color: '#6dbf7e' }}>.</span>
          </div>
        </div>
      )}

      {/* Sidebar overlay (mobile) */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199 }}
        />
      )}

      {/* Sidebar */}
      <div style={isMobile ? {
        ...s.sidebar,
        position: 'fixed',
        top: 0, bottom: 0, left: 0,
        zIndex: 200,
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s ease',
        boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.3)' : 'none',
      } : s.sidebar}>
        <div style={s.sidebarTop}>
          <div style={s.logo}>
            Lumumba<span style={{ color: '#6dbf7e' }}>.</span>
          </div>
          <div style={s.logoSub}>E-mail Portaal</div>
        </div>

        <nav style={s.nav}>
          {navItems.map(item => (
            <button
              key={item.id}
              style={{
                ...s.navItem,
                ...(view === item.id ? s.navItemActive : {}),
              }}
              onClick={() => handleNav(item.id)}
            >
              <span style={s.navIcon}>{item.icon}</span>
              <span style={s.navLabel}>{item.label}</span>
              {item.badge ? <span style={s.navBadge}>{item.badge}</span> : null}
            </button>
          ))}
        </nav>

        <div style={s.sidebarFooter}>
          <div style={s.userBlock}>
            <div style={s.userName}>{user.name}</div>
            <div style={s.userRole}>{ROLE_LABELS[user.role] || user.role}</div>
          </div>
          <button style={s.logoutBtn} onClick={logout}>Uitloggen</button>
        </div>
      </div>

      {/* Main content */}
      <div style={isMobile ? { ...s.main, paddingTop: 56 } : s.main}>
        {view === 'dashboard'  && renderDashboard()}
        {view === 'compose'    && renderCompose()}
        {view === 'drafts'     && renderDrafts()}
        {view === 'verzonden'  && renderVerzonden()}
        {view === 'ontvangers' && renderOntvangers()}
        {view === 'beheer'     && user.role === 'admin' && <AdminPage />}
      </div>
    </div>
  );
}

// ─── Sidebar styles ─────────────────────────────────────────────────────────────
const s = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  sidebar: {
    width: 228,
    minWidth: 228,
    background: '#1a2a1e',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
  },
  sidebarTop: {
    padding: '28px 20px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  logo: {
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    fontWeight: 'bold',
    color: '#fff',
    letterSpacing: '-0.3px',
  },
  logoSub: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.35)',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginTop: 3,
  },
  nav: {
    flex: 1,
    padding: '16px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '11px 12px',
    minHeight: 44,
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: 'rgba(255,255,255,0.55)',
    fontSize: 13.5,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    width: '100%',
    transition: 'background 0.15s',
  },
  navItemActive: {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    fontWeight: 600,
  },
  navIcon: {
    fontSize: 16,
    width: 20,
    textAlign: 'center',
    flexShrink: 0,
  },
  navLabel: { flex: 1 },
  navBadge: {
    background: '#3a7a4a',
    color: '#fff',
    borderRadius: 10,
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 700,
  },
  sidebarFooter: {
    padding: '16px 12px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  userBlock: {
    padding: '8px 10px 12px',
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
  },
  userRole: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.4)',
    marginTop: 2,
  },
  logoutBtn: {
    width: '100%',
    padding: '9px 12px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 7,
    color: 'rgba(255,255,255,0.5)',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'center',
  },
  main: {
    flex: 1,
    background: '#f0ede8',
    minHeight: '100vh',
    overflowY: 'auto',
  },
};

// ─── View / content styles ──────────────────────────────────────────────────────
const v = {
  wrap: {
    maxWidth: 780,
    margin: '0 auto',
    padding: '40px 32px',
  },
  pageHeader: {
    marginBottom: 28,
  },
  pageTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 26,
    fontWeight: 600,
    color: '#1a2a1e',
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 14,
    color: '#aaa',
  },

  // Dashboard
  welcomeBanner: {
    background: '#1a2a1e',
    borderRadius: 16,
    padding: '28px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 20,
    flexWrap: 'wrap',
  },
  welcomeTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    color: '#fff',
    fontWeight: 600,
    marginBottom: 4,
  },
  welcomeSub: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
  },
  newEmailBtn: {
    background: '#3a7a4a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 22px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    whiteSpace: 'nowrap',
  },
  statsRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  statCard: {
    flex: 1,
    minWidth: 120,
    background: '#fff',
    borderRadius: 12,
    padding: '20px 20px 16px',
    border: '1px solid #ede9e3',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  statNum: {
    fontFamily: 'Georgia, serif',
    fontSize: 30,
    fontWeight: 600,
    color: '#1a2a1e',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: 10,
  },
  historyRow: {
    background: '#fff',
    borderRadius: 10,
    padding: '14px 18px',
    marginBottom: 8,
    border: '1px solid #ede9e3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  historyLeft: { flex: 1 },
  historySubject: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a2a1e',
    marginBottom: 2,
  },
  historyMeta: {
    fontSize: 12,
    color: '#aaa',
  },
  historyDate: {
    fontSize: 12,
    color: '#bbb',
    whiteSpace: 'nowrap',
  },
  resumeBtn: {
    background: 'transparent',
    border: '1px solid #3a7a4a',
    borderRadius: 6,
    padding: '7px 14px',
    fontSize: 12,
    color: '#3a7a4a',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  deleteBtn: {
    background: 'transparent',
    border: '1px solid #ffcccc',
    borderRadius: 6,
    padding: '7px 14px',
    fontSize: 12,
    color: '#cc0000',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  emptyCard: {
    background: '#fff',
    borderRadius: 12,
    padding: '40px',
    textAlign: 'center',
    color: '#aaa',
    fontSize: 14,
    border: '1px solid #ede9e3',
  },
  centeredCard: {
    background: '#fff',
    borderRadius: 16,
    padding: '60px 40px',
    textAlign: 'center',
    border: '1px solid #ede9e3',
    boxShadow: '0 4px 24px rgba(0,0,0,.06)',
  },

  // Type select
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16,
  },
  typeCard: {
    background: '#fff',
    border: '1px solid #ede9e3',
    borderRadius: 14,
    padding: '28px 24px',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    position: 'relative',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  typeCardAvailable: {
    borderColor: '#ede9e3',
    boxShadow: '0 2px 8px rgba(0,0,0,.04)',
  },
  typeCardDisabled: {
    opacity: 0.55,
    cursor: 'default',
    background: '#faf9f7',
  },
  comingSoonBadge: {
    position: 'absolute',
    top: 14,
    right: 14,
    background: '#f0ede8',
    color: '#999',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.5px',
    padding: '3px 8px',
    borderRadius: 4,
    textTransform: 'uppercase',
  },
  typeCardLabel: {
    fontFamily: 'Georgia, serif',
    fontSize: 18,
    fontWeight: 600,
    color: '#1a2a1e',
    marginBottom: 8,
  },
  typeCardDesc: {
    fontSize: 13,
    color: '#888',
    lineHeight: 1.5,
  },

  // Compose form
  backLink: {
    background: 'transparent',
    border: '1px solid #e0dcd6',
    borderRadius: 7,
    padding: '8px 14px',
    fontSize: 13,
    color: '#666',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  savedIndicator: {
    fontSize: 12,
    color: '#6dbf7e',
    marginTop: 2,
  },
  formCard: {
    background: '#fff',
    borderRadius: 16,
    padding: '32px 36px',
    boxShadow: '0 4px 24px rgba(0,0,0,.06)',
    border: '1px solid #ede9e3',
  },
  field: { marginBottom: 20 },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    marginBottom: 6,
    letterSpacing: '.02em',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1px solid #e0dcd6',
    borderRadius: 8,
    fontSize: 15,
    color: '#1a2a1e',
    background: '#faf8f5',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '11px 14px',
    border: '1px solid #e0dcd6',
    borderRadius: 8,
    fontSize: 15,
    color: '#1a2a1e',
    background: '#faf8f5',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
    lineHeight: 1.7,
    boxSizing: 'border-box',
  },
  error: {
    background: '#fff0f0',
    border: '1px solid #ffcccc',
    borderRadius: 8,
    padding: '12px 14px',
    fontSize: 14,
    color: '#cc0000',
    marginBottom: 16,
  },
  primaryBtn: {
    background: '#3a7a4a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 22px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    letterSpacing: '.02em',
  },
  secondaryBtn: {
    background: 'transparent',
    color: '#555',
    border: '1px solid #e0dcd6',
    borderRadius: 8,
    padding: '12px 22px',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Preview
  previewToolbar: {
    background: '#fff',
    borderRadius: 12,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,.06)',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  previewMeta: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    padding: '0 8px',
  },
  previewSubject: { fontSize: 14, fontWeight: 600, color: '#1a2a1e' },
  previewNote: { fontSize: 12, color: '#f0a500' },
  previewFrame: {
    width: '100%',
    height: '800px',
    border: '1px solid #e0dcd6',
    borderRadius: 12,
    background: '#fff',
  },

  // Mailing list checklist
  listChecklist: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: '#faf8f5',
    border: '1px solid #e0dcd6',
    borderRadius: 8,
    padding: '12px 14px',
  },
  listCheckRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    userSelect: 'none',
  },
  listCheckName: {
    flex: 1,
    fontSize: 14,
    color: '#1a2a1e',
  },
  listCheckCount: {
    fontSize: 12,
    color: '#aaa',
    whiteSpace: 'nowrap',
  },

  // Leaderboard editor (uitslag)
  lbTable: {
    border: '1px solid #e0dcd6',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#faf8f5',
  },
  lbHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: '#1a2a1e',
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  lbRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderTop: '1px solid #e0dcd6',
    background: '#fff',
  },
  lbColRank: { width: 28, textAlign: 'center', flexShrink: 0, fontSize: 13, color: '#aaa', fontWeight: 700 },
  lbColTeam: { flex: 1, fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' },
  lbColPoule: { width: 80, fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' },
  lbColScore: { width: 80, fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' },
  lbInput: {
    padding: '7px 10px',
    border: '1px solid #e0dcd6',
    borderRadius: 6,
    fontSize: 14,
    color: '#1a2a1e',
    background: '#faf8f5',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  lbRemoveBtn: {
    width: 28,
    height: 28,
    flexShrink: 0,
    background: 'transparent',
    border: '1px solid #ffcccc',
    borderRadius: 6,
    color: '#cc0000',
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    fontFamily: 'inherit',
  },
  lbAddBtn: {
    display: 'block',
    width: '100%',
    padding: '10px',
    background: 'transparent',
    border: 'none',
    borderTop: '1px solid #e0dcd6',
    color: '#3a7a4a',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'center',
  },

  // Sent
  successIcon: {
    width: 64, height: 64, borderRadius: '50%', background: '#3a7a4a',
    color: '#fff', fontSize: 28, display: 'flex', alignItems: 'center',
    justifyContent: 'center', margin: '0 auto 20px',
  },
  successTitle: {
    fontFamily: 'Georgia, serif', fontSize: 26,
    color: '#1a2a1e', marginBottom: 12,
  },
  successText: {
    fontSize: 15, color: '#888', lineHeight: 1.7, marginBottom: 28,
  },
};

// ─── Modal styles ────────────────────────────────────────────────────────────────
const m = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  box: {
    background: '#fff',
    borderRadius: 16,
    padding: '32px 36px',
    maxWidth: 420,
    width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  title: {
    fontFamily: 'Georgia, serif',
    fontSize: 20,
    fontWeight: 600,
    color: '#1a2a1e',
    marginBottom: 12,
  },
  text: {
    fontSize: 14,
    color: '#555',
    lineHeight: 1.6,
    marginBottom: 24,
  },
  actions: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    background: 'transparent',
    border: '1px solid #e0dcd6',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  confirmBtn: {
    background: '#cc0000',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    cursor: 'pointer',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
};
